import { useEffect } from 'react';
import useContractStore from '@/app/store/modules/contract';
import useCustomizationStore from '@/app/store/modules/customization';
import useEnvelopesStore from '@/app/store/modules/envelopes';
import useMerchantAccountStore from '@/app/store/modules/merchantAccount';
import useMerchantUserStore from '@/app/store/modules/merchantUser';
import useOrganizationsStore from '@/app/store/modules/organizations';
import usePlansStore from '@/app/store/modules/plan';
import usePushPaymentStore from '@/app/store/modules/pushPayment';
import useRemoteCollectionStore from '@/app/store/modules/remoteCollection';
import useSimsStore from '@/app/store/modules/sims';
import useSubscriptionStore from '@/app/store/modules/subscription';
import useTerminalStore from '@/app/store/modules/terminal';
import useUserStore from '@/app/store/modules/user';
import useSearchContractsStore from '@/app/store/modules/searchContracts';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import RouteUrls from '../enums/RouteUrls';
import useHistoricsStore from '@/app/store/modules/historics';


/**
 * Hook to reset table filters based on the route base url
 * @param root
 * @param {Object} root.routeBaseUrl - The route base url
 * @returns {void}
 */
const useResetTableFilters = ({ routeBaseUrl }) => {
    // Store
    // Contracts
    const storeQueryContracts = useContractStore(s => s.storeQuery);
    // Customization
    const storeQueryCustomization = useCustomizationStore(s => s.storeQuery);
    // Envelopes
    const storeQueryEnvelopes = useEnvelopesStore(s => s.storeQueryEnvelopes);
    const storeQueryDownloadCodes = useEnvelopesStore(s => s.storeQueryDownloadCodes);
    // Merchant Accounts
    const storeQueryMerchantAccounts = useMerchantAccountStore(s => s.storeQuery);
    // Merchant Users
    const storeQueryMerchantUsers = useMerchantUserStore(s => s.storeQuery);
    // Organizations
    const storeQueryOrganizations = useOrganizationsStore(s => s.storeQuery);
    // Plans
    const storeQueryPlans = usePlansStore(s => s.storeQuery);
    // Push Payments
    const storeQueryPartners = usePushPaymentStore(s => s.storeQueryPartners);
    const storeQueryNotifRules = usePushPaymentStore(s => s.storeQueryNotifRules);
    // Remote Collection
    const storeQueryRemittance = useRemoteCollectionStore(s => s.storeQuery);
    // Sims
    const storeQuerySimCards = useSimsStore(s => s.storeQuerySimCards);
    const storeQuerySimGroups = useSimsStore(s => s.storeQuerySimGroups);
    // Subscriptions
    const storeQuerySubscriptions = useSubscriptionStore(s => s.storeQuery);
    const storeQueryPayluddSubscriptions = useSubscriptionStore(s => s.storeQueryPayluddSubscriptions);
    // Terminal
    const storeQueryEventLogs = useTerminalStore(s => s.storeQueryEventLogs);
    const storeQueryTransactions = useTerminalStore(s => s.storeQueryTransactions);
    // Users
    const storeQueryUsers = useUserStore(s => s.storeQuery);
    // Search contracts
    const storeQuerySearchContracts = useSearchContractsStore(s => s.storeQuerySearchContracts);
    // Historics
    const storeQueryUsersHistorics = useHistoricsStore(s => s.storeQueryUsersHistorics);
    const storeQueryPosTerminalsHistorics = useHistoricsStore(s => s.storeQueryPosTerminalsHistorics);
    const storeQuerySimCardsHistorics = useHistoricsStore(s => s.storeQuerySimCardsHistorics);

    useEffect(() => {
        // Reset table filters based on the route base url
        // Main pages - Reset all table filters besides those of the table in the main page
        if (routeBaseUrl !== RouteUrls.ENVELOPES.url) { storeQueryEnvelopes(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.ENVELOPES.url) { storeQueryDownloadCodes(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.MERCHANT_ACCOUNTS.url) { storeQueryMerchantAccounts(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.ORGANIZATIONS.url) { storeQueryOrganizations(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.PLANS.url) { storeQueryPlans(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.PUSH_PAYMENTS.url) { storeQueryPartners(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.PUSH_PAYMENTS.url) { storeQueryNotifRules(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.REMOTE_COLLECTION.url) { storeQueryRemittance(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.SIM_CARDS.url) { storeQuerySimCards(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.SIM_CARDS.url) { storeQuerySimGroups(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.SUBSCRIPTIONS.url) { storeQuerySubscriptions(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.USERS.url) { storeQueryUsers(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.SEARCH_CONTRACTS.url) { storeQuerySearchContracts(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.HISTORICS.url) { storeQueryUsersHistorics(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.HISTORICS.url) { storeQueryPosTerminalsHistorics(getInitialQuery()); }
        if (routeBaseUrl !== RouteUrls.HISTORICS.url) { storeQuerySimCardsHistorics(getInitialQuery()); }

        // Details pages and others - Reset all table filters from details pages and others
        storeQueryContracts(getInitialQuery());
        storeQueryCustomization(getInitialQuery());
        storeQueryMerchantUsers(getInitialQuery());
        storeQueryPayluddSubscriptions(getInitialQuery());
        storeQueryEventLogs(getInitialQuery());
        storeQueryTransactions(getInitialQuery());
    }, [routeBaseUrl]);
};

export default useResetTableFilters;